import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, MailOpen } from "lucide-react";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/dentlab/src/components/ui/tooltip";
import {
	HoverCard,
	HoverCardTrigger,
	HoverCardContent,
} from "@/dentlab/src/components/ui/hover-card";
import { supabase, Tables } from "@/lib/supabase";
import { ProfilesForChat } from "@/lib/types/profile.types";
import { JobRequestChannel } from "@/lib/types/job-list.types";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { InboxHoverMessages } from "./inbox-hover-messages.component";
import { getMessagePreview, formatRelativeTime } from "./inbox.utils";
import { JobRequestItem } from "@/dentlab/src/components/inbox/job-request-item.component";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { JobRequestCustomV1, MessageType } from "@/lib/store/Realtime/message.types";
import { showNotification } from "@/lib/notification";

type Channel = Tables<"message_channels">;

interface InboxWidgetProps {
	channels: Channel[];
	getNavigationPath: (channel: Channel) => string;
	onChannelClick?: (channel: Channel) => void;
	onMarkAsRead?: (channelId: string) => void;
	organizationId?: string;
	getOrgName?: (orgId: string) => string | undefined;
	userId?: string;
	className?: string;
	profilesForChat?: Record<string, ProfilesForChat>;
	hideSubtitle?: boolean;
	showJobRequestsFilter?: boolean;
	jobRequestChannels?: JobRequestChannel[];
}

const getChannelTitle = (
	channel: Channel,
	organizationId?: string,
	getOrgName?: (id: string) => string | undefined
): string => {
	if (channel.channel_type === "general") {
		const otherOrgId =
			channel.customer_org_id === organizationId
				? channel.vendor_org_id
				: channel.customer_org_id;
		const prefix =
			channel.customer_org_id === organizationId ? "Labor:" : "Kunde:";
		const name = otherOrgId && getOrgName?.(otherOrgId);
		return `${prefix} ${name || "Chat"}`;
	}
	if (channel.channel_type === "job_request" && !channel.job_id) {
		return "Neue Auftragsanfrage";
	}
	if (channel.job_title) {
		return channel.job_title;
	}
	return "Chat";
};

const getChannelSubtitle = (
	channel: Channel,
	organizationId?: string,
	getOrgName?: (id: string) => string | undefined
): string => {
	const otherOrgId =
		channel.customer_org_id === organizationId
			? channel.vendor_org_id
			: channel.customer_org_id;

	if (channel.job_id && channel.channel_type === "job") {
		return (otherOrgId && getOrgName?.(otherOrgId)) || "";
	}
	if (channel.channel_type === "general") {
		return channel.customer_org_id === organizationId
			? "Allgemeiner Chat mit Labor"
			: "Allgemeiner Chat mit Kunde";
	}
	if (channel.channel_type === "job_request" && !channel.job_id) {
		return (otherOrgId && getOrgName?.(otherOrgId)) || "";
	}
	return "";
};

export const InboxWidget: React.FC<InboxWidgetProps> = ({
	channels,
	getNavigationPath,
	onChannelClick,
	onMarkAsRead,
	organizationId,
	getOrgName,
	userId,
	className,
	profilesForChat,
	hideSubtitle,
	showJobRequestsFilter,
	jobRequestChannels,
}) => {
	const navigate = useNavigate();
	const acceptJobRequest = useJobStore((s) => s.acceptJobRequest);
	const denyJobRequest = useJobStore((s) => s.denyJobRequest);
	const fetchJobRequestChannels = useJobStore(
		(s) => s.fetchJobRequestChannels
	);
	const [jobRequestsFilterActive, setJobRequestsFilterActive] = useState(false);

	const unreadChannels = useMemo(() => {
		return channels
			.filter((ch) => (ch.unread_count || 0) > 0)
			.sort((a, b) => {
				const aTime = a.last_message_at
					? new Date(a.last_message_at).getTime()
					: 0;
				const bTime = b.last_message_at
					? new Date(b.last_message_at).getTime()
					: 0;
				return bTime - aTime;
			});
	}, [channels]);

	const handleClick = (channel: Channel) => {
		if (onChannelClick) {
			onChannelClick(channel);
			return;
		}
		navigate(getNavigationPath(channel));
	};

	const handleAcceptJobRequest = async (channelId: string) => {
		const { data } = await supabase
			.from("messages")
			.select("content")
			.eq("channel_id", channelId)
			.order("created_at", { ascending: true })
			.limit(1);

		const content = data?.[0]
			?.content as unknown as JobRequestCustomV1 | null;

		const result = await acceptJobRequest(
			channelId,
			content?.type === MessageType.JOB_REQUEST_CUSTOM_V1 ? content : null
		);

		if (result?.success) {
			fetchJobRequestChannels();
			showNotification({
				message: "Auftragsanfrage akzeptiert",
				type: "success",
				action: {
					label: "Zum Auftrag",
					onClick: () =>
						navigate(
							`/client/${result.clientId}/job/${result.jobId}`
						),
				},
			});
		}
	};

	const handleDeclineJobRequest = async (channelId: string) => {
		const success = await denyJobRequest(channelId);
		if (success) {
			fetchJobRequestChannels();
			showNotification({
				message: "Auftragsanfrage abgelehnt",
				type: "info",
			});
		}
	};

	const showingJobRequests = showJobRequestsFilter && jobRequestsFilterActive;
	const displayCount = showingJobRequests
		? (jobRequestChannels?.length ?? 0)
		: unreadChannels.length;

	return (
		<div
			className={cn(
				"bg-white rounded-lg border border-gray-200 flex flex-col overflow-hidden",
				className
			)}
			data-testid="inbox-widget"
		>
			<div className="flex items-center gap-2 px-3 pt-2.5 pb-2 flex-shrink-0 bg-gray-50">
				<Mail className="h-4 w-4 text-gray-500" />
				<h3 className="text-sm font-semibold text-gray-900">
					{showingJobRequests ? "Offene Auftragsanfragen" : "Ungelesene Nachrichten"}
				</h3>
				{displayCount > 0 && (
					<span className="text-xs font-medium text-gray-500">
						{displayCount}
					</span>
				)}
				{showJobRequestsFilter && (
					<button
						className={cn(
							"ml-auto text-xs px-2 py-0.5 rounded-full border transition-colors",
							jobRequestsFilterActive
								? "bg-primary text-primary-foreground border-primary"
								: "bg-white text-gray-500 border-gray-300 hover:border-gray-400"
						)}
						onClick={() => setJobRequestsFilterActive(!jobRequestsFilterActive)}
						aria-label="Auftragsanfragen filtern"
						data-testid="inbox-job-requests-filter"
					>
						Offene Auftragsanfragen
						{(jobRequestChannels?.length ?? 0) > 0 && (
							<span className="ml-1">
								{jobRequestChannels!.length}
							</span>
						)}
					</button>
				)}
			</div>

			<div className="border-t border-gray-200" />

			{showingJobRequests ? (
				(jobRequestChannels?.length ?? 0) === 0 ? (
					<div className="flex-1 flex items-center justify-center pb-3">
						<div className="text-center">
							<Mail className="h-6 w-6 mx-auto mb-1 text-gray-300" />
							<p className="text-xs text-gray-400">
								Keine offenen Auftragsanfragen
							</p>
						</div>
					</div>
				) : (
					<div className="flex-1 min-h-0 overflow-y-auto">
						{jobRequestChannels!.map((ch) => {
							const customerName =
								ch.customerOrgId && getOrgName?.(ch.customerOrgId);
							const matchingChannel = channels.find((c) => c.id === ch.channelId);
							return (
								<React.Fragment key={ch.channelId}>
									<JobRequestItem
										channelId={ch.channelId}
										title={ch.title}
										customerName={customerName ?? undefined}
										onAccept={handleAcceptJobRequest}
										onDecline={handleDeclineJobRequest}
										onClick={matchingChannel ? () => handleClick(matchingChannel) : undefined}
									/>
								</React.Fragment>
							);
						})}
					</div>
				)
			) : unreadChannels.length === 0 ? (
				<div className="flex-1 flex items-center justify-center pb-3">
					<div className="text-center">
						<Mail className="h-6 w-6 mx-auto mb-1 text-gray-300" />
						<p className="text-xs text-gray-400">
							Keine ungelesenen Nachrichten
						</p>
					</div>
				</div>
			) : (
				<div className="flex-1 min-h-0 overflow-y-auto">
						{unreadChannels.map((channel) => {
							const isPendingJobRequest =
								channel.channel_type === "job_request" && !channel.job_id;
							const weAreTheVendor = channel.vendor_org_id === organizationId;

							if (isPendingJobRequest && channel.id && weAreTheVendor) {
								const otherOrgId =
									channel.customer_org_id === organizationId
										? channel.vendor_org_id
										: channel.customer_org_id;
								const orgName = otherOrgId && getOrgName?.(otherOrgId);
								return (
									<React.Fragment key={channel.id}>
										<JobRequestItem
											channelId={channel.id}
											title={channel.job_title}
											customerName={orgName ?? undefined}
											onAccept={handleAcceptJobRequest}
											onDecline={handleDeclineJobRequest}
											onClick={() => handleClick(channel)}
										/>
									</React.Fragment>
								);
							}

							const title = getChannelTitle(
								channel,
								organizationId,
								getOrgName
							);
							const subtitle = getChannelSubtitle(
								channel,
								organizationId,
								getOrgName
							);
							const preview = getMessagePreview(
								channel.last_message_content,
								{ userId, senderId: channel.last_message_created_by }
							);

							const row = (
								<div
									key={channel.id}
									className="px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0 group"
									onClick={() => handleClick(channel)}
									data-testid={`inbox-channel-${channel.id}`}
									aria-label={`Chat \u00f6ffnen: ${title}`}
								>
									<div className="flex items-center justify-between gap-2">
										<span className="text-sm font-semibold text-gray-900 truncate">
											{title}
											{(channel.unread_count || 0) > 1 && (
												<span className="text-[11px] font-normal text-gray-400 ml-1">
													{channel.unread_count}
												</span>
											)}
										</span>
										<div className="flex items-center gap-1.5 flex-shrink-0">
											{onMarkAsRead && (
												<Tooltip>
													<TooltipTrigger asChild>
														<button
															className="h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
															onClick={(e) => {
																e.stopPropagation();
																if (channel.id) onMarkAsRead(channel.id);
															}}
															aria-label="Als gelesen markieren"
															data-testid={`mark-as-read-${channel.id}`}
														>
															<MailOpen className="h-3.5 w-3.5" />
														</button>
													</TooltipTrigger>
													<TooltipContent side="left">
														Als gelesen markieren
													</TooltipContent>
												</Tooltip>
											)}
											<span className="text-[11px] text-gray-400">
												{formatRelativeTime(
													channel.last_message_at
												)}
											</span>
										</div>
									</div>
									{!hideSubtitle && subtitle && (
										<p className="text-[13px] text-gray-500 truncate">
											{subtitle}
										</p>
									)}
									{preview && (
										<p className="text-[13px] text-gray-400 truncate">
											{preview}
										</p>
									)}
								</div>
							);

							if (!profilesForChat || !channel.id) {
								return row;
							}

							return (
								<HoverCard
									key={channel.id}
									openDelay={700}
									closeDelay={100}
								>
									<HoverCardTrigger asChild>
										{row}
									</HoverCardTrigger>
									<HoverCardContent
										side="right"
										className="w-80 max-h-72 flex flex-col p-0"
									>
										<div className="flex-1 min-h-0 overflow-y-auto p-3">
											<InboxHoverMessages
												channelId={channel.id}
												lastReadAt={channel.last_read_at ?? null}
												userId={userId}
												profilesForChat={profilesForChat}
											/>
										</div>
										{onMarkAsRead && (
											<div className="sticky bottom-0 border-t border-gray-200 bg-white p-2">
												<button
													className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 rounded hover:bg-gray-100 transition-colors"
													onClick={() => {
														if (channel.id) onMarkAsRead(channel.id);
													}}
													aria-label="Als gelesen markieren"
													data-testid={`hover-mark-as-read-${channel.id}`}
												>
													<MailOpen className="h-3.5 w-3.5" />
													Als gelesen markieren
												</button>
											</div>
										)}
									</HoverCardContent>
								</HoverCard>
							);
						})}
				</div>
			)}
		</div>
	);
};
